import { useForm } from '@tanstack/react-form';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type {
  FilterForm,
  FilterGroupItem,
  FilterItem,
  FilterLineItem,
  FilterShortcut,
  FilterType,
  SavedFilter,
} from '../../utils/filterSettings';
import { cleanFilterGroup } from '../../utils/savedFilters';
import { Button } from '../Button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter } from '../Dialog';
import { Icon } from '../Icon';
import { Text } from '../Text';
import { AdvancedFilterGroup } from './AdvancedFilterGroup';
import { SavedFilters, SaveFilterButton } from './SavedFilters';
import { parseFieldPath } from './utils';

export type AdvancedFilterDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: FilterGroupItem;
  onFiltersChange: (filters: FilterGroupItem) => void;
  /**
   * Filter types available for filtering (typically derived from table columns)
   */
  filterTypes: FilterType[];
  filterShortcuts?: FilterShortcut[];
  /** User-saved filter sets. Each applies like a built-in shortcut. */
  savedFilters?: SavedFilter[];
  /** Save the current filter set under a name. When omitted, the save control is hidden. */
  onSaveFilter?: (name: string, filter: FilterGroupItem) => void;
  /** Remove a saved filter by id. */
  onRemoveSavedFilter?: (id: string) => void;
};

function AdvancedFilterDialog({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  filterTypes,
  filterShortcuts,
  savedFilters,
  onSaveFilter,
  onRemoveSavedFilter,
}: AdvancedFilterDialogProps) {
  // Default empty filter structure
  const createDefaultFilters = useCallback(
    (): FilterGroupItem => ({
      type: 'group',
      id: 'root',
      operator: 'and',
      items: [
        {
          type: 'line',
          id: `line-${Date.now()}-${Math.random()}`,
          operator: 'and',
        },
      ],
    }),
    [],
  );
  const defaultFilters: FilterGroupItem = useMemo(() => createDefaultFilters(), [createDefaultFilters]);

  // Use stateControl filters if provided, otherwise use local state
  // If stateControl filters are empty on root, set initial line as default filter
  const currentFilters = filters && filters.items.length > 0 ? filters : defaultFilters;

  // Initialize TanStack Form
  // For recursive types with complex generics, we use type inference with a minimal interface
  // This avoids the "excessively deep" error by not requiring TypeScript to resolve all nested generics
  // We cast to our FilterForm interface which only includes the methods we actually use
  // Filters are stored locally in the form and only applied to URL/stateControl on "Apply" click
  const formBase = useForm({
    defaultValues: currentFilters,
    onSubmit: async ({ value }: { value: FilterGroupItem }) => {
      // Apply filters to stateControl/URL only when "Apply" is clicked
      const filteredFilters = cleanFilterGroup(value);
      onFiltersChange(filteredFilters);
      onOpenChange(false);
    },
  });

  // Type assertion to our minimal interface to avoid recursive type inference
  // This is safe because FilterForm only includes methods that exist on the actual form
  const form = formBase as unknown as FilterForm;

  // Read through a ref so a new `filters` reference on a parent re-render (e.g. a window-focus refetch) can't reset in-progress edits.
  const currentFiltersRef = useRef(currentFilters);
  currentFiltersRef.current = currentFilters;

  // Load the applied filters into the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      form.reset(currentFiltersRef.current);
    }
  }, [open, form]);

  // Handler to delete an item at any level using its fieldPath
  const handleDeleteItem = useCallback(
    (fieldPath: string) => {
      const { itemsPath, index } = parseFieldPath(fieldPath);
      if (itemsPath === null || index === null) return;

      const currentItems = (form.getFieldValue(itemsPath) as FilterItem[]) || [];
      form.setFieldValue(
        itemsPath,
        currentItems.filter((_: FilterItem, i: number) => i !== index),
      );
    },
    [form],
  );

  // Handler to duplicate an item at any level using its fieldPath
  const handleDuplicateItem = useCallback(
    (fieldPath: string) => {
      const { itemsPath, index } = parseFieldPath(fieldPath);
      if (itemsPath === null || index === null) return;

      const currentItems = (form.getFieldValue(itemsPath) as FilterItem[]) || [];
      const itemToDuplicate = currentItems[index];
      if (itemToDuplicate) {
        const duplicated: FilterItem = {
          ...itemToDuplicate,
          id: `${itemToDuplicate.type}-${Date.now()}-${Math.random()}`,
        };
        const newItems = [...currentItems];
        newItems.splice(index + 1, 0, duplicated);
        form.setFieldValue(itemsPath, newItems);
      }
    },
    [form],
  );

  // Handler to convert a line item to a group at any level using its fieldPath
  const handleMakeLineGroup = useCallback(
    (fieldPath: string) => {
      const { itemsPath, index } = parseFieldPath(fieldPath);
      if (itemsPath === null || index === null) return;

      const currentItems = (form.getFieldValue(itemsPath) as FilterItem[]) || [];
      const itemToConvert = currentItems[index];
      if (itemToConvert && itemToConvert.type === 'line') {
        const newGroup: FilterGroupItem = {
          type: 'group',
          id: `group-${Date.now()}-${Math.random()}`,
          operator: 'and',
          items: [itemToConvert],
        };
        const newItems = [...currentItems];
        newItems[index] = newGroup;
        form.setFieldValue(itemsPath, newItems);
      }
    },
    [form],
  );

  // Handler to wrap an existing group in a new parent group at the same level
  // The existing group and all its children move down one level and become items[0] of the new group
  const handleMakeGroupGroup = useCallback(
    (fieldPath: string) => {
      const { itemsPath, index } = parseFieldPath(fieldPath);
      if (itemsPath === null || index === null) return;

      // Get the current group item at the given field path
      const currentGroup = (form.getFieldValue(fieldPath) as FilterGroupItem) || null;
      if (currentGroup) {
        // Create a new parent group with the current group as the first item
        const newParentGroup: FilterGroupItem = {
          type: 'group',
          id: `group-${Date.now()}-${Math.random()}`,
          operator: 'and',
          items: [currentGroup],
        };
        // Set the new parent group at the given field path
        form.setFieldValue(fieldPath, newParentGroup);
      }
    },
    [form],
  );

  // Handler to add a filter line to a group at any level using its fieldPath
  // This gets added to the items array of the group it is called on
  // e.g., '' -> adds to the items array of the root 'items' group
  // e.g., 'items[0]' -> adds to the 'items[0].items' group
  const handleAddFilter = useCallback(
    (fieldPath: string) => {
      const { itemsPath, index } = parseFieldPath(fieldPath);
      const childItemsPath = itemsPath && index !== null ? `${itemsPath}[${index}].items` : 'items';
      const items = (form.getFieldValue(childItemsPath) as FilterItem[]) || [];
      const newLine: FilterLineItem = {
        type: 'line',
        id: `line-${Date.now()}-${Math.random()}`,
        operator: 'and',
      };
      form.setFieldValue(childItemsPath, [...items, newLine]);
    },
    [form],
  );

  // Helper function to check if form values differ from defaultFilters
  const isDifferentFromDefault = useCallback((filters: FilterGroupItem): boolean => {
    // Check if operator is different (default is 'and')
    if ((filters.operator || 'and') !== 'and') {
      return true;
    }

    // Check if items count is different (default has 1 item)
    if (filters.items.length !== 1) {
      return true;
    }

    // Check if the single item is an empty line (default state)
    const singleItem = filters.items[0];
    if (singleItem.type === 'line') {
      // An empty line has no filterType, operatorType, or value
      return !!(singleItem.filterType || singleItem.operatorType || singleItem.value);
    }

    // If it's a group, it's different from default
    if (singleItem.type === 'group') {
      return true;
    }

    return false;
  }, []);

  // Root-level handlers
  const handleClearAll = useCallback(() => {
    const freshDefaults = createDefaultFilters();
    form.setFieldValue('items', freshDefaults.items || []);
    form.setFieldValue('operator', freshDefaults.operator || 'and');
  }, [form, createDefaultFilters]);

  const handleApplyShortcut = useCallback(
    (shortcut: FilterShortcut) => {
      const filterGroup = shortcut.getFilter();
      form.setFieldValue('items', filterGroup.items || []);
      form.setFieldValue('operator', filterGroup.operator || 'and');
    },
    [form],
  );

  // Save the current (in-progress) filter set under a name. Capture the live form value
  // and clean it so the saved set matches what "Apply" would persist.
  const handleSaveCurrent = useCallback(
    (name: string) => {
      if (!onSaveFilter) return;
      const cleaned = cleanFilterGroup(form.state.values);
      onSaveFilter(name, cleaned);
    },
    [form, onSaveFilter],
  );

  const handleApply = useCallback(() => {
    form.handleSubmit();
  }, [form]);

  const handleCancel = useCallback(() => {
    form.reset(currentFilters);
    onOpenChange(false);
  }, [form, currentFilters, onOpenChange]);

  // The first section in the dialog body uses -mt-4 to cancel the body's top padding.
  const hasSavedFilters = !!savedFilters && savedFilters.length > 0 && !!onRemoveSavedFilter;
  const hasShortcuts = !!filterShortcuts && filterShortcuts.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="advanced-filter-dialog"
        className="max-h-[80vh] w-fit min-w-[610px] max-w-[95vw]"
        title="Filters"
      >
        <DialogDescription className="sr-only">
          Configure advanced filters for the table. Add, remove, and organize filter conditions.
        </DialogDescription>
        <DialogBody className="align-start flex flex-col gap-2 overflow-x-auto">
          {hasSavedFilters && onRemoveSavedFilter && (
            <SavedFilters
              savedFilters={savedFilters ?? []}
              onApply={handleApplyShortcut}
              onRemove={onRemoveSavedFilter}
            />
          )}
          {hasShortcuts && (
            <div className={`border-grey-20 grid grid-cols-3 gap-2 border-b pb-3${hasSavedFilters ? '' : ' -mt-4'}`}>
              {filterShortcuts.map((shortcut) => (
                <Button
                  key={shortcut.label}
                  type="button"
                  variant="secondary"
                  size="small"
                  className="py-1 text-xs"
                  onClick={() => handleApplyShortcut(shortcut)}
                >
                  {shortcut.label}
                </Button>
              ))}
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="flex flex-col gap-2"
          >
            <div className="flex flex-col">
              <form.Field name="items">
                {(field: any) => {
                  const items = (field.state.value as FilterItem[]) || [];
                  const hasMultipleItems = items.length > 1;
                  return (
                    <>
                      {items.map((item: FilterItem, index: number) => {
                        const isFirstItem = index === 0;
                        // When there are multiple items, first item gets the selector, others get operator text
                        // When there's only one item, no operator is shown
                        const shouldShowSelector = hasMultipleItems && isFirstItem;
                        const shouldShowOperatorText = hasMultipleItems && !isFirstItem;
                        const fieldPath = `items[${index}]`;

                        return (
                          <form.Field key={item.id} name={fieldPath}>
                            {(itemField: any) => (
                              <AdvancedFilterGroup
                                form={form}
                                fieldPath={fieldPath}
                                itemType={itemField.state?.value?.type || 'line'}
                                filterTypes={filterTypes}
                                shouldShowOperatorSelector={shouldShowSelector}
                                shouldShowOperatorText={shouldShowOperatorText}
                                depth={0}
                                onMakeLineGroup={handleMakeLineGroup}
                                onMakeGroupGroup={handleMakeGroupGroup}
                                onDelete={handleDeleteItem}
                                onDuplicate={handleDuplicateItem}
                                onAddFilter={handleAddFilter}
                              />
                            )}
                          </form.Field>
                        );
                      })}
                    </>
                  );
                }}
              </form.Field>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-5">
              <Button
                type="button"
                variant="hollow"
                size="small"
                className="h-6 gap-1 px-px py-0 text-xs font-medium text-blue-50"
                onClick={() => handleAddFilter('')}
              >
                <Icon icon="plus" size={14} />
                <Text className="text-xs text-blue-50">Add Filter</Text>
              </Button>
              <form.Subscribe selector={(state) => state.values}>
                {(filters) => {
                  return (
                    isDifferentFromDefault(filters as FilterGroupItem) && (
                      <Button
                        type="button"
                        variant="hollow"
                        size="small"
                        className="h-6 gap-1 px-px py-0 text-xs font-medium text-red-50"
                        onClick={handleClearAll}
                      >
                        <Icon icon="trash" size={14} />
                        <Text className="text-xs text-red-50">Clear All Filters</Text>
                      </Button>
                    )
                  );
                }}
              </form.Subscribe>
              {onSaveFilter && <SaveFilterButton savedFilters={savedFilters ?? []} onSave={handleSaveCurrent} />}
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <form.Subscribe selector={(state) => state.isDirty}>
            {(isDirty) => (
              <Button type="submit" onClick={handleApply} disabled={!isDirty}>
                Apply
              </Button>
            )}
          </form.Subscribe>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { AdvancedFilterDialog };
